import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useToast } from '@/shared/hooks/useToast';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import { updateTaskEntity } from './useTaskEntityCache';
import { useProjectStatuses } from './useProjectStatuses';
import type { PlannedDate } from '@/shared/types/datetime';
import type { Task, TaskPriority, UpdateTaskDetailPayload } from '../types';

interface UseTaskFieldUpdateOptions {
  projectId: string;
  boardId?: string;
}

/**
 * Returns 7 handlers that each update a single task field. Every handler applies an
 * optimistic patch via the entity cache (propagating to all active list/board/calendar
 * queries), calls the update mutation, and reverts + shows an error toast on failure.
 *
 * Status and group changes can move a task between board columns/sections — the entity
 * cache cannot express that re-grouping, so those two handlers fall back to invalidating
 * the broad `projectRoot` prefix after settle instead of (or in addition to) the optimistic
 * patch. All other fields are fully covered by `updateTaskEntity` and need no invalidation.
 */
export function useTaskFieldUpdate({ projectId, boardId }: UseTaskFieldUpdateOptions) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');
  const { toast } = useToast();
  const { statuses } = useProjectStatuses(boardId ?? '');

  const { mutateAsync: doUpdateTask } = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: UpdateTaskDetailPayload }) =>
      tasksService.update(taskId, data),
  });

  function getPrevTask(id: string): Task | undefined {
    return queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, id));
  }

  function invalidateProjectRoot() {
    void queryClient.invalidateQueries({
      queryKey: TASK_KEYS.projectRoot(orgId, projectId),
      type: 'active',
    });
  }

  function showUpdateError() {
    toast.error({ title: t('apiErrors.updateDefault') });
  }

  async function handleTitleSave(id: string, title: string) {
    const prev = getPrevTask(id);
    updateTaskEntity(queryClient, orgId, id, (t) => ({ ...t, title }));
    try {
      await doUpdateTask({ taskId: id, data: { title } });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    }
  }

  async function handleStatusChange(id: string, statusId: string) {
    const prev = getPrevTask(id);
    const status = statuses.find((s) => s.id === statusId);
    if (status) {
      updateTaskEntity(queryClient, orgId, id, (t) => ({ ...t, status }));
    }
    try {
      await doUpdateTask({ taskId: id, data: { statusId } });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    } finally {
      // The full status object (color/order/wipLimit) may not be resolvable when no
      // boardId is available, and a status change can move the task between board
      // columns — invalidate the broad projectRoot scope so every view re-syncs.
      invalidateProjectRoot();
    }
  }

  async function handlePriorityChange(id: string, priority: TaskPriority | null) {
    const prev = getPrevTask(id);
    updateTaskEntity(queryClient, orgId, id, (t) => ({ ...t, priority: priority ?? undefined }));
    try {
      // priority: null clears the field; undefined is omitted by JSON.stringify — TODO: verify endpoint behavior
      await doUpdateTask({ taskId: id, data: { priority: priority } });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    }
  }

  async function handleAssigneeChange(id: string, assigneeId: string | null) {
    const prev = getPrevTask(id);
    updateTaskEntity(queryClient, orgId, id, (t) => ({
      ...t,
      assignee: assigneeId ? { id: assigneeId, name: '—', color: '' } : null,
    }));
    try {
      await doUpdateTask({ taskId: id, data: { assigneeId } });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    } finally {
      // The optimistic patch above is a placeholder (name: '—') — the real
      // assignee's name/avatar only exist server-side, so re-sync from the
      // server or the placeholder sticks in the row forever.
      invalidateProjectRoot();
    }
  }

  async function handleDateChange(id: string, start: PlannedDate | null, end: PlannedDate | null) {
    const prev = getPrevTask(id);
    updateTaskEntity(queryClient, orgId, id, (t) => ({
      ...t,
      startDate: start,
      dueDate: end,
    }));
    try {
      await doUpdateTask({
        taskId: id,
        data: { startDate: start, dueDate: end },
      });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    }
  }

  async function handleEstimatedChange(id: string, minutes: number | null) {
    const prev = getPrevTask(id);
    updateTaskEntity(queryClient, orgId, id, (t) => ({ ...t, estimatedMinutes: minutes }));
    try {
      await doUpdateTask({ taskId: id, data: { estimatedMinutes: minutes } });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    }
  }

  async function handleGroupChange(id: string, groupId: string | null) {
    const prev = getPrevTask(id);
    updateTaskEntity(queryClient, orgId, id, (t) => ({ ...t, groupId: groupId ?? undefined }));
    try {
      await doUpdateTask({ taskId: id, data: { groupId } });
    } catch {
      if (prev) updateTaskEntity(queryClient, orgId, id, () => prev);
      showUpdateError();
    } finally {
      // Group changes can move a task between board sections — the entity cache patch
      // above does not re-sort grouped collections, so invalidate projectRoot to be safe.
      invalidateProjectRoot();
    }
  }

  return {
    handleTitleSave,
    handleStatusChange,
    handlePriorityChange,
    handleAssigneeChange,
    handleDateChange,
    handleEstimatedChange,
    handleGroupChange,
  };
}
